import React from "react";

import { AssistantToolUseContent } from "@/AgentThread/AgentThreadSidebar/AgentThreadByIdView/AgentThreadMessage/AssistantToolUseContent.tsx";
import {
  AgentThreadMessage,
  AgentThreadRole,
  isAgentThreadTextContent,
  isAgentThreadToolUseContent,
  isAgentThreadToolResultContent,
  isAgentThreadErrorContent,
  AgentThreadMessageStatus,
} from "@/shared/domain/agentThread/AgentThreadRecord";

import styles from "./AgentThreadMessageComponents.module.css";
import { AssistantErrorContent } from "./AssistantErrorContent";
import { AssistantTextContent } from "./AssistantTextContent";
import { AssistantToolResultContent } from "./AssistantToolResultContent";
import { UserMessage } from "./UserMessage";

interface AgentThreadMessageComponentsProps {
  message: AgentThreadMessage;
  isLastMessageInAgentThread: boolean;
  onLinkClick?: () => void;
  onRetry?: () => void;
  isCancelling?: boolean;
  /** Tool use ID currently awaiting approval — its "Using Tool" indicator is hidden. */
  pendingApprovalToolUseId?: string;
}

const AgentThreadMessageComponents = ({
  message,
  onLinkClick,
  onRetry,
  isLastMessageInAgentThread,
  isCancelling = false,
  pendingApprovalToolUseId,
}: AgentThreadMessageComponentsProps) => {
  const components: React.ReactNode[] = [];
  let msgIdx = 0;

  if (message.role === AgentThreadRole.USER) {
    message.content.forEach((content) => {
      if (isAgentThreadTextContent(content)) {
        const isFailed = message.status === AgentThreadMessageStatus.FAILED;

        components.push(
          <UserMessage
            key={msgIdx++}
            text={content.text}
            isFailed={isFailed}
            onRetry={isFailed ? onRetry : undefined}
          />,
        );
      }
    });
  }

  if (message.role === AgentThreadRole.ASSISTANT) {
    message.content.forEach((content, idx) => {
      const isLastContentInMessage = idx === message.content.length - 1;
      const isLastContentInAgentThread =
        isLastContentInMessage && isLastMessageInAgentThread;

      if (isAgentThreadTextContent(content)) {
        components.push(
          <AssistantTextContent
            key={msgIdx++}
            text={content.text}
            isGenerating={
              isLastContentInAgentThread &&
              message.status === AgentThreadMessageStatus.GENERATING
            }
            isCancelling={isLastContentInAgentThread && isCancelling}
            onLinkClick={onLinkClick}
          />,
        );
      }

      if (isAgentThreadToolUseContent(content)) {
        if (
          isLastMessageInAgentThread &&
          content.tool_use_id !== pendingApprovalToolUseId
        ) {
          components.push(
            <AssistantToolUseContent key={msgIdx++} toolUse={content} />,
          );
        }
      }

      if (isAgentThreadErrorContent(content)) {
        components.push(
          <AssistantErrorContent key={msgIdx++} error={content} />,
        );
      }
    });
  }

  if (message.role === AgentThreadRole.ROBOTO) {
    // Roboto-role messages carry tool_result blocks and the occasional
    // generation error. Roboto-role text chunks are internal scaffolding —
    // ctx markers and the like — and stay hidden by design.
    message.content.forEach((content) => {
      if (isAgentThreadToolResultContent(content)) {
        components.push(
          <AssistantToolResultContent key={msgIdx++} toolResult={content} />,
        );
      }

      if (isAgentThreadErrorContent(content)) {
        components.push(
          <AssistantErrorContent key={msgIdx++} error={content} />,
        );
      }
    });
  }

  if (components.length === 0) {
    return null;
  }

  return <div className={styles.mainContainer}>{components}</div>;
};

export { AgentThreadMessageComponents };
